import React, { useMemo, useState } from "react";
import { deleteRow, getDocumentDownloadUrl, insertRow, updateRow } from "../lib/data";
import { useI18n } from "../lib/i18n";
import { Icon } from "./Icons";
import { CaseInvoices } from "./InvoiceManagement";

export const CASE_STATUSES = [
  { value: "INTAKE", label: "Pranim", short: "Pranim" },
  { value: "ACTIVE", label: "Në punë", short: "Aktive" },
  { value: "WAITING_CLIENT", label: "Pret klientin", short: "Pret klientin" },
  { value: "WAITING_COURT", label: "Pret gjykatën", short: "Pret gjykatën" },
  { value: "HEARING", label: "Seancë", short: "Seancë" },
  { value: "COMPLETED", label: "Përfunduar", short: "Përfunduar" },
  { value: "CLOSED", label: "Mbyllur", short: "Mbyllur" },
];

const PRIORITIES = [
  { value: "LOW", label: "I ulët" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "I lartë" },
  { value: "URGENT", label: "Urgjent" },
];

const TASK_STATUSES = [
  { value: "TODO", label: "Për t’u bërë" },
  { value: "IN_PROGRESS", label: "Në punë" },
  { value: "DONE", label: "Përfunduar" },
  { value: "CANCELLED", label: "Anuluar" },
];

function localDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
}

function isoOrNull(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function formatDate(value, time = false, locale = "sq-AL", noDeadline = "Pa afat") {
  if (!value) return noDeadline;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return noDeadline;
  return new Intl.DateTimeFormat(locale, time
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date);
}

function statusLabel(value) {
  return CASE_STATUSES.find((item) => item.value === value)?.label || value || "—";
}

function priorityLabel(value) {
  return PRIORITIES.find((item) => item.value === value)?.label || value || "Normal";
}

function staffLabel(staff, userId) {
  if (!userId) return "Pa caktuar";
  const member = staff.find((item) => item.user_id === userId);
  return member?.display_name || member?.email || "Përdorues";
}

function isOpenTask(task) {
  return ["TODO", "IN_PROGRESS"].includes(task.status);
}

function isOpenCase(item) {
  return !["COMPLETED", "CLOSED"].includes(item.status);
}

function isOverdue(value) {
  return value && new Date(value).getTime() < Date.now();
}

function SetupNotice({ dataset }) {
  if (dataset?.table) return null;
  return (
    <section className="case-setup-notice">
      <span className="icon-badge"><Icon name="warning" size={23} /></span>
      <div>
        <strong>Aktivizoni menaxhimin e rasteve</strong>
        <p>Ekzekutoni <code>supabase/FULL_SUPABASE_SETUP_ADMIN_ONLY.sql</code> në projektin e saktë Supabase. Pastaj rifreskoni këtë panel.</p>
        {dataset?.error?.message ? <small>{dataset.error.message}</small> : null}
      </div>
    </section>
  );
}

function CaseBadge({ item }) {
  return <span className={`case-priority case-priority--${String(item.priority || "NORMAL").toLowerCase()}`}>{priorityLabel(item.priority)}</span>;
}

function CaseCard({ item, tasks, staff, onOpen, locale = "sq-AL", noDeadline = "Pa afat" }) {
  const caseTasks = tasks.filter((task) => task.case_id === item.id);
  const openTasks = caseTasks.filter(isOpenTask);
  const overdue = openTasks.filter((task) => isOverdue(task.due_at)).length;
  const completed = caseTasks.filter((task) => task.status === "DONE").length;
  const progress = Number(item.progress_percent || (caseTasks.length ? Math.round((completed / caseTasks.length) * 100) : 0));
  return (
    <button className="case-card" type="button" onClick={() => onOpen(item.id)}>
      <div className="case-card__top"><strong>{item.reference}</strong><CaseBadge item={item} /></div>
      <h3>{item.title}</h3>
      <p>{item.client_name}</p>
      <div className="case-card__progress"><i><b style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} /></i><span>{progress}%</span></div>
      <div className="case-card__meta">
        <span><Icon name="user" size={13} /> {staffLabel(staff, item.assigned_to)}</span>
        <span className={overdue ? "case-card__overdue" : ""}><Icon name="calendar" size={13} /> {overdue ? `${overdue} vonuar` : (item.next_action_at ? formatDate(item.next_action_at, false, locale, noDeadline) : `${openTasks.length} detyra`)}</span>
      </div>
    </button>
  );
}

function Pipeline({ cases, tasks, staff, onOpen, locale, noDeadline }) {
  return (
    <div className="case-board" aria-label="Tabela e fazave të rasteve">
      {CASE_STATUSES.map((stage) => {
        const rows = cases.filter((item) => item.status === stage.value);
        return (
          <section className="case-column" key={stage.value}>
            <header><span className={`case-stage-dot case-stage-dot--${stage.value.toLowerCase()}`} /><strong>{stage.short}</strong><small>{rows.length}</small></header>
            <div className="case-column__cards">
              {rows.length ? rows.map((item) => <CaseCard key={item.id} item={item} tasks={tasks} staff={staff} onOpen={onOpen} locale={locale} noDeadline={noDeadline} />) : <div className="case-column__empty">Asnjë rast</div>}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Dashboard({ data, onNavigate, onOpen }) {
  const { locale, t } = useI18n();
  const cases = data.cases.rows;
  const tasks = data.tasks.rows;
  const staff = data.staff.rows;
  const openCases = cases.filter(isOpenCase);
  const overdueTasks = tasks.filter((task) => isOpenTask(task) && isOverdue(task.due_at));
  const unassigned = cases.filter((item) => item.status === "INTAKE" && !item.assigned_to);
  const sevenDays = Date.now() + (7 * 24 * 60 * 60 * 1000);
  const deadlines = cases
    .filter((item) => isOpenCase(item) && item.next_action_at && new Date(item.next_action_at).getTime() <= sevenDays)
    .sort((a, b) => new Date(a.next_action_at) - new Date(b.next_action_at));

  const stages = CASE_STATUSES.map((stage) => ({ ...stage, count: cases.filter((item) => item.status === stage.value).length }));
  const maximum = Math.max(...stages.map((stage) => stage.count), 1);

  return (
    <>
      <SetupNotice dataset={data.cases} />
      {data.cases.table ? (
        <>
          <section className="case-kpi-grid">
            <button type="button" onClick={() => onNavigate("cases")}><span className="case-kpi__icon"><Icon name="briefcase" size={20} /></span><strong>{openCases.length}</strong><span>Raste aktive</span><small>{cases.length} gjithsej</small></button>
            <button type="button" onClick={() => onNavigate("tasks")}><span className="case-kpi__icon case-kpi__icon--danger"><Icon name="warning" size={20} /></span><strong>{overdueTasks.length}</strong><span>Detyra të vonuara</span><small>Kërkojnë veprim</small></button>
            <button type="button" onClick={() => onNavigate("cases")}><span className="case-kpi__icon"><Icon name="message" size={20} /></span><strong>{unassigned.length}</strong><span>Pranime pa përgjegjës</span><small>Caktoni pronarin</small></button>
            <button type="button" onClick={() => onNavigate("tasks")}><span className="case-kpi__icon"><Icon name="calendar" size={20} /></span><strong>{deadlines.length}</strong><span>Afate në 7 ditë</span><small>Planifikoni javën</small></button>
          </section>

          <div className="case-dashboard-grid">
            <section className="admin-panel case-overview-panel">
              <div className="admin-panel__heading"><div><span>Portofoli</span><h2>Shpërndarja e rasteve</h2></div><button type="button" onClick={() => onNavigate("cases")}>Hap tabelën</button></div>
              <div className="case-stage-list">
                {stages.map((stage) => (
                  <button type="button" key={stage.value} onClick={() => onNavigate("cases")}>
                    <span>{stage.label}</span><i><b style={{ width: `${Math.max((stage.count / maximum) * 100, stage.count ? 7 : 0)}%` }} /></i><strong>{stage.count}</strong>
                  </button>
                ))}
              </div>
            </section>
            <section className="admin-panel case-deadline-panel">
              <div className="admin-panel__heading"><div><span>Prioritetet</span><h2>Afatet e ardhshme</h2></div><button type="button" onClick={() => onNavigate("tasks")}>Të gjitha detyrat</button></div>
              <div className="case-deadline-list">
                {deadlines.length ? deadlines.slice(0, 6).map((item) => (
                  <button type="button" key={item.id} onClick={() => onOpen(item.id)}>
                    <time className={isOverdue(item.next_action_at) ? "is-overdue" : ""}>{formatDate(item.next_action_at, false, locale, t("Pa afat"))}</time>
                    <span><strong>{item.reference}</strong>{item.title}</span><CaseBadge item={item} />
                  </button>
                )) : <div className="case-empty-state"><Icon name="check" size={24} /><span>Nuk ka afate të afërta.</span></div>}
              </div>
            </section>
          </div>

          <section className="admin-panel case-recent-panel">
            <div className="admin-panel__heading"><div><span>Aktiviteti</span><h2>Rastet e përditësuara së fundi</h2></div><button type="button" onClick={() => onNavigate("cases")}>Shiko të gjitha</button></div>
            <div className="case-recent-grid">
              {cases.slice(0, 6).map((item) => <CaseCard key={item.id} item={item} tasks={tasks} staff={staff} onOpen={onOpen} locale={locale} noDeadline={t("Pa afat")} />)}
              {!cases.length ? <div className="case-empty-state">Rasti i parë do të shfaqet këtu sapo klienti të dërgojë formularin.</div> : null}
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}

function NewCaseForm({ userId, onClose, onSaved, notify }) {
  const [draft, setDraft] = useState({ title: "", client_name: "", client_email: "", client_phone: "", category: "", description: "", priority: "NORMAL" });
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await insertRow("cases", { ...draft, reference: null, status: "INTAKE", created_by: userId });
      notify("success", `Rasti ${created.reference} u krijua.`);
      await onSaved(created.id);
    } catch (error) {
      notify("error", error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="case-modal" role="dialog" aria-modal="true" aria-label="Krijo rast të ri">
      <button className="case-modal__backdrop" type="button" onClick={onClose} aria-label="Mbyll" />
      <form className="case-modal__card" onSubmit={submit}>
        <header><div><span>Regjistrim manual</span><h2>Rast i ri</h2></div><button type="button" onClick={onClose}><Icon name="close" size={19} /></button></header>
        <label>Titulli i rastit<input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required /></label>
        <div className="form-row"><label>Emri i klientit<input value={draft.client_name} onChange={(event) => setDraft({ ...draft, client_name: event.target.value })} required /></label><label>Kategoria<input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></label></div>
        <div className="form-row"><label>Email<input type="email" value={draft.client_email} onChange={(event) => setDraft({ ...draft, client_email: event.target.value })} /></label><label>Telefon<input value={draft.client_phone} onChange={(event) => setDraft({ ...draft, client_phone: event.target.value })} /></label></div>
        <label>Prioriteti<select value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>{PRIORITIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label>Përshkrimi<textarea rows="5" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
        <button className="button button--gold button--wide" type="submit" disabled={busy}>{busy ? "Duke krijuar…" : "Krijo dhe cakto referencën"}</button>
      </form>
    </div>
  );
}

function CasesWorkspace({ data, userId, selectedCaseId, onOpen, onRefresh, notify }) {
  const { locale, t } = useI18n();
  const [query, setQuery] = useState("");
  const [priority, setPriority] = useState("ALL");
  const [assignee, setAssignee] = useState("ALL");
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const cases = data.cases.rows;
  const staff = data.staff.rows;
  const filtered = useMemo(() => cases.filter((item) => {
    const haystack = `${item.reference} ${item.title} ${item.client_name} ${item.client_email || ""} ${item.client_phone || ""}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (priority === "ALL" || item.priority === priority)
      && (assignee === "ALL" || (assignee === "UNASSIGNED" ? !item.assigned_to : item.assigned_to === assignee));
  }), [cases, query, priority, assignee]);

  return (
    <>
      <SetupNotice dataset={data.cases} />
      {data.cases.table ? (
        <>
          <section className="case-toolbar">
            <label className="case-search"><Icon name="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Kërko referencë, klient ose titull…" /></label>
            <select aria-label="Filtro prioritetin" value={priority} onChange={(event) => setPriority(event.target.value)}><option value="ALL">Çdo prioritet</option>{PRIORITIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            <select aria-label="Filtro përgjegjësin" value={assignee} onChange={(event) => setAssignee(event.target.value)}><option value="ALL">Çdo përgjegjës</option><option value="UNASSIGNED">Pa caktuar</option>{staff.map((item) => <option key={item.id} value={item.user_id}>{item.display_name || item.email}</option>)}</select>
            <button className="button button--gold button--small" type="button" onClick={() => setNewCaseOpen(true)}><Icon name="plus" size={15} /> Rast i ri</button>
          </section>
          <div className="case-board-summary"><strong>{filtered.length}</strong> raste në pamjen aktuale <span>•</span> Klikoni një kartë për dosjen e plotë</div>
          <Pipeline cases={filtered} tasks={data.tasks.rows} staff={staff} onOpen={onOpen} locale={locale} noDeadline={t("Pa afat")} />
        </>
      ) : null}
      {newCaseOpen ? <NewCaseForm userId={userId} onClose={() => setNewCaseOpen(false)} notify={notify} onSaved={async (id) => { setNewCaseOpen(false); await onRefresh(); onOpen(id); }} /> : null}
      {selectedCaseId ? <CaseDrawer caseId={selectedCaseId} data={data} userId={userId} onClose={() => onOpen(null)} onRefresh={onRefresh} notify={notify} /> : null}
    </>
  );
}

function TimeEntries({ caseItem, dataset, userId, onRefresh, notify }) {
  const { locale, t } = useI18n();
  const [draft, setDraft] = useState({
    entry_date: new Date().toISOString().slice(0, 10),
    description: "",
    minutes: 60,
    billable: true,
    hourly_rate: "",
  });
  const [busy, setBusy] = useState(false);
  const rows = (dataset?.rows || []).filter((row) => row.case_id === caseItem.id);
  const totalMinutes = rows.reduce((sum, row) => sum + Number(row.minutes || 0), 0);
  const billableValue = rows.reduce((sum, row) => sum + (row.billable ? (Number(row.minutes || 0) / 60) * Number(row.hourly_rate || 0) : 0), 0);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await insertRow("case_time_entries", {
        case_id: caseItem.id,
        entry_date: draft.entry_date,
        description: draft.description.trim(),
        minutes: Number(draft.minutes),
        billable: draft.billable,
        hourly_rate: draft.billable && draft.hourly_rate !== "" ? Number(draft.hourly_rate) : null,
        created_by: userId,
      });
      setDraft((old) => ({ ...old, description: "", minutes: 60 }));
      notify("success", t("Koha u regjistrua."));
      await onRefresh();
    } catch (error) { notify("error", error.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await deleteRow("case_time_entries", id); notify("success", t("Regjistrimi i kohës u fshi.")); await onRefresh(); }
    catch (error) { notify("error", error.message); }
  };

  if (!dataset?.table) return <div className="case-empty-state">{t("Ekzekutoni skedarin e ri SQL për të aktivizuar regjistrimin e kohës.")}</div>;
  return (
    <div className="case-tab-stack time-workspace">
      <section className="time-summary">
        <article><span>{t("Koha totale")}</span><strong>{(totalMinutes / 60).toFixed(2)} h</strong></article>
        <article><span>{t("Vlera e faturueshme")}</span><strong>{new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(billableValue)}</strong></article>
      </section>
      <form className="time-entry-form" onSubmit={save}>
        <label>{t("Data")}<input type="date" value={draft.entry_date} onChange={(event) => setDraft({ ...draft, entry_date: event.target.value })} required /></label>
        <label className="time-entry-form__description">{t("Përshkrimi")}<input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder={t("Puna e kryer…")} required /></label>
        <label>{t("Minuta")}<input type="number" min="1" max="1440" value={draft.minutes} onChange={(event) => setDraft({ ...draft, minutes: event.target.value })} required /></label>
        <label>{t("Tarifa për orë")}<input type="number" min="0" step="0.01" value={draft.hourly_rate} onChange={(event) => setDraft({ ...draft, hourly_rate: event.target.value })} disabled={!draft.billable} /></label>
        <label className="admin-checkbox"><input type="checkbox" checked={draft.billable} onChange={(event) => setDraft({ ...draft, billable: event.target.checked })} /><span>{t("E faturueshme")}</span></label>
        <button className="button button--gold button--small" type="submit" disabled={busy}>{busy ? t("Duke ruajtur…") : t("Shto kohën")}</button>
      </form>
      <div className="time-entry-list">
        {rows.map((row) => <article key={row.id}><div><strong>{row.description}</strong><span>{formatDate(row.entry_date, false, locale, t("Pa afat"))} · {(Number(row.minutes) / 60).toFixed(2)} h · {row.billable ? t("E faturueshme") : t("Jo e faturueshme")}</span></div><div><strong>{row.hourly_rate ? new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format((Number(row.minutes) / 60) * Number(row.hourly_rate)) : "—"}</strong><button className="icon-action icon-action--danger" type="button" onClick={() => remove(row.id)} aria-label={t("Hiq")}><Icon name="trash" size={15} /></button></div></article>)}
        {!rows.length ? <div className="case-empty-state">{t("Nuk ka regjistrime kohe për këtë rast.")}</div> : null}
      </div>
    </div>
  );
}

function CaseDrawer({ caseId, data, userId, onClose, onRefresh, notify }) {
  const { locale, t } = useI18n();
  const item = data.cases.rows.find((row) => row.id === caseId);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [draft, setDraft] = useState(() => item ? {
    title: item.title || "", category: item.category || "", status: item.status || "INTAKE",
    priority: item.priority || "NORMAL", assigned_to: item.assigned_to || "",
    next_action_at: localDateTime(item.next_action_at), court_name: item.court_name || "",
    court_case_number: item.court_case_number || "", opposing_party: item.opposing_party || "",
    description: item.description || "", progress_percent: Number(item.progress_percent || 0),
    billing_type: item.billing_type || "FIXED", estimated_fee: item.estimated_fee ?? "",
    retainer_amount: item.retainer_amount ?? "", client_company_name: item.client_company_name || "",
    client_registration_number: item.client_registration_number || "", client_fiscal_number: item.client_fiscal_number || "",
    client_vat_number: item.client_vat_number || "", client_address: item.client_address || "",
    billing_email: item.billing_email || item.client_email || "",
  } : {});

  if (!item) return null;
  const tasks = data.tasks.rows.filter((row) => row.case_id === caseId);
  const notes = data.notes.rows.filter((row) => row.case_id === caseId);
  const documents = data.caseDocuments.rows.filter((row) => row.case_id === caseId);
  const events = data.caseEvents.rows.filter((row) => row.case_id === caseId);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await updateRow("cases", item.id, {
        ...draft,
        assigned_to: draft.assigned_to || null,
        next_action_at: isoOrNull(draft.next_action_at),
        progress_percent: Number(draft.progress_percent || 0),
        estimated_fee: draft.estimated_fee === "" ? 0 : Number(draft.estimated_fee),
        retainer_amount: draft.retainer_amount === "" ? 0 : Number(draft.retainer_amount),
        closed_at: ["COMPLETED", "CLOSED"].includes(draft.status) ? (item.closed_at || new Date().toISOString()) : null,
      });
      notify("success", `Rasti ${item.reference} u përditësua.`);
      await onRefresh();
    } catch (error) { notify("error", error.message); } finally { setBusy(false); }
  };

  const addEvent = async (eventType, title) => {
    try { await insertRow("case_events", { case_id: caseId, event_type: eventType, title, actor_user_id: userId }); } catch { /* Primary record remains authoritative. */ }
  };

  const addTask = async (event) => {
    event.preventDefault();
    if (!taskTitle.trim()) return;
    try {
      await insertRow("case_tasks", { case_id: caseId, title: taskTitle.trim(), due_at: isoOrNull(taskDue), priority: item.priority || "NORMAL", assigned_to: item.assigned_to || userId, created_by: userId });
      await addEvent("TASK", `Detyrë e re: ${taskTitle.trim()}`);
      setTaskTitle(""); setTaskDue(""); notify("success", "Detyra u shtua."); await onRefresh();
    } catch (error) { notify("error", error.message); }
  };

  const setTaskStatus = async (task, status) => {
    try {
      await updateRow("case_tasks", task.id, { status, completed_at: status === "DONE" ? new Date().toISOString() : null });
      notify("success", "Detyra u përditësua."); await onRefresh();
    } catch (error) { notify("error", error.message); }
  };

  const addNote = async (event) => {
    event.preventDefault();
    if (!noteBody.trim()) return;
    try {
      await insertRow("case_notes", { case_id: caseId, body: noteBody.trim(), note_type: "NOTE", created_by: userId });
      await addEvent("NOTE", "Shënim i brendshëm u shtua");
      setNoteBody(""); notify("success", "Shënimi u shtua."); await onRefresh();
    } catch (error) { notify("error", error.message); }
  };

  const download = async (document) => {
    try {
      const url = await getDocumentDownloadUrl({ bucket: document.bucket, path: document.path, name: document.name });
      const link = window.document.createElement("a"); link.href = url; link.rel = "noreferrer"; link.click();
    } catch (error) { notify("error", error.message); }
  };

  return (
    <div className="case-drawer" role="dialog" aria-modal="true" aria-label={`Dosja ${item.reference}`}>
      <button className="case-drawer__backdrop" type="button" onClick={onClose} aria-label="Mbyll dosjen" />
      <aside className="case-drawer__panel">
        <header className="case-drawer__header">
          <div><span>{item.reference}</span><h2>{item.title}</h2><p>{item.client_name} • {statusLabel(item.status)}</p></div>
          <button type="button" onClick={onClose} aria-label="Mbyll"><Icon name="close" size={21} /></button>
        </header>
        <nav className="case-tabs">
          {[['overview', 'Përmbledhje'], ['tasks', `Detyrat (${tasks.length})`], ['time', 'Koha'], ['invoices', `Fatura (${data.invoices.rows.filter((row) => row.case_id === caseId).length})`], ['notes', `Shënime (${notes.length})`], ['documents', `Dokumente (${documents.length})`], ['timeline', 'Aktiviteti']].map(([value, label]) => <button className={tab === value ? "active" : ""} type="button" key={value} onClick={() => setTab(value)}>{label}</button>)}
        </nav>

        <div className="case-drawer__body">
          {tab === "overview" ? (
            <form className="case-detail-form" onSubmit={save}>
              <div className="case-client-card"><span className="icon-badge"><Icon name="user" size={21} /></span><div><strong>{item.client_name}</strong><a href={`tel:${item.client_phone || ""}`}>{item.client_phone || "Pa telefon"}</a><a href={`mailto:${item.client_email || ""}`}>{item.client_email || "Pa email"}</a><small>{t("Gjuha e preferuar")}: {item.preferred_language === "sr" ? "Srpski" : item.preferred_language === "en" ? "English" : "Shqip"}</small></div></div>
              <label>Titulli<input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required /></label>
              <div className="form-row"><label>Statusi<select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>{CASE_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label><label>Prioriteti<select value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>{PRIORITIES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label></div>
              <div className="form-row"><label>Përgjegjësi<select value={draft.assigned_to} onChange={(event) => setDraft({ ...draft, assigned_to: event.target.value })}><option value="">Pa caktuar</option>{data.staff.rows.map((member) => <option key={member.id} value={member.user_id}>{member.display_name || member.email}</option>)}</select></label><label>Veprimi / afati i ardhshëm<input type="datetime-local" value={draft.next_action_at} onChange={(event) => setDraft({ ...draft, next_action_at: event.target.value })} /></label></div>
              <section className="case-project-panel">
                <div className="case-progress-control"><label>Progresi <strong>{draft.progress_percent}%</strong></label><input type="range" min="0" max="100" step="5" value={draft.progress_percent} onChange={(event) => setDraft({ ...draft, progress_percent: Number(event.target.value) })} /><i><b style={{ width: `${draft.progress_percent}%` }} /></i></div>
                <div className="form-row"><label>Lloji i faturimit<select value={draft.billing_type} onChange={(event) => setDraft({ ...draft, billing_type: event.target.value })}><option value="FIXED">Tarifë fikse</option><option value="HOURLY">Sipas orëve</option><option value="MILESTONE">Sipas fazës</option><option value="OTHER">Tjetër</option></select></label><label>Buxheti i rastit<input type="number" min="0" step="0.01" value={draft.estimated_fee} onChange={(event) => setDraft({ ...draft, estimated_fee: event.target.value })} /></label></div>
                <label>Parapagimi<input type="number" min="0" step="0.01" value={draft.retainer_amount} onChange={(event) => setDraft({ ...draft, retainer_amount: event.target.value })} /></label>
              </section>
              <label>Kategoria<input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></label>
              <div className="form-row"><label>Gjykata<input value={draft.court_name} onChange={(event) => setDraft({ ...draft, court_name: event.target.value })} /></label><label>Numri gjyqësor<input value={draft.court_case_number} onChange={(event) => setDraft({ ...draft, court_case_number: event.target.value })} /></label></div>
              <label>Pala kundërshtare<input value={draft.opposing_party} onChange={(event) => setDraft({ ...draft, opposing_party: event.target.value })} /></label>
              <section className="case-billing-client"><h3>Të dhënat e klientit për faturë</h3><div className="form-row"><label>Emri i kompanisë<input value={draft.client_company_name} onChange={(event) => setDraft({ ...draft, client_company_name: event.target.value })} /></label><label>Email për faturim<input type="email" value={draft.billing_email} onChange={(event) => setDraft({ ...draft, billing_email: event.target.value })} /></label></div><label>Adresa e faturimit<input value={draft.client_address} onChange={(event) => setDraft({ ...draft, client_address: event.target.value })} /></label><div className="form-row"><label>Numri unik i biznesit<input value={draft.client_registration_number} onChange={(event) => setDraft({ ...draft, client_registration_number: event.target.value })} /></label><label>Numri fiskal<input value={draft.client_fiscal_number} onChange={(event) => setDraft({ ...draft, client_fiscal_number: event.target.value })} /></label></div><label>Numri i TVSH-së<input value={draft.client_vat_number} onChange={(event) => setDraft({ ...draft, client_vat_number: event.target.value })} /></label></section>
              <label>Përshkrimi<textarea rows="6" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
              <button className="button button--gold button--wide" type="submit" disabled={busy}>{busy ? "Duke ruajtur…" : "Ruaj dosjen"}</button>
            </form>
          ) : null}

          {tab === "tasks" ? <div className="case-tab-stack"><form className="case-quick-add" onSubmit={addTask}><input value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} placeholder="Detyrë e re…" required /><input type="datetime-local" value={taskDue} onChange={(event) => setTaskDue(event.target.value)} /><button className="button button--gold button--small" type="submit"><Icon name="plus" size={14} /> Shto</button></form><div className="case-task-list">{tasks.map((task) => <article key={task.id} className={isOpenTask(task) && isOverdue(task.due_at) ? "is-overdue" : ""}><button className={`task-check ${task.status === "DONE" ? "is-done" : ""}`} type="button" onClick={() => setTaskStatus(task, task.status === "DONE" ? "TODO" : "DONE")}><Icon name="check" size={14} /></button><div><strong>{task.title}</strong><span>{formatDate(task.due_at, true, locale, t("Pa afat"))} • {staffLabel(data.staff.rows, task.assigned_to)}</span></div><select value={task.status} onChange={(event) => setTaskStatus(task, event.target.value)}>{TASK_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></article>)}{!tasks.length ? <div className="case-empty-state">Nuk ka detyra për këtë rast.</div> : null}</div></div> : null}

          {tab === "time" ? <TimeEntries caseItem={item} dataset={data.timeEntries} userId={userId} onRefresh={onRefresh} notify={notify} /> : null}

          {tab === "invoices" ? <CaseInvoices caseItem={item} data={data} userId={userId} onRefresh={onRefresh} notify={notify} /> : null}

          {tab === "notes" ? <div className="case-tab-stack"><form className="case-note-form" onSubmit={addNote}><textarea rows="4" value={noteBody} onChange={(event) => setNoteBody(event.target.value)} placeholder="Shënim i brendshëm për ekipin…" required /><button className="button button--gold button--small" type="submit">Shto shënimin</button></form><div className="case-note-list">{notes.map((note) => <article key={note.id}><header><strong>{note.note_type}</strong><time>{formatDate(note.created_at, true, locale, t("Pa afat"))}</time></header><p>{note.body}</p></article>)}{!notes.length ? <div className="case-empty-state">Nuk ka shënime të brendshme.</div> : null}</div></div> : null}

          {tab === "documents" ? <div className="case-document-list">{documents.map((document) => <button key={document.id} type="button" onClick={() => download(document)}><span className="icon-badge"><Icon name="document" size={20} /></span><span><strong>{document.name}</strong><small>{(Number(document.size_bytes || 0) / 1024 / 1024).toFixed(2)} MB • Lidhje private 60 sekonda</small></span><Icon name="download" size={18} /></button>)}{!documents.length ? <div className="case-empty-state">Klienti nuk ka bashkëngjitur dokumente.</div> : null}</div> : null}

          {tab === "timeline" ? <div className="case-timeline">{events.map((event) => <article key={event.id}><i /><div><strong>{event.title}</strong><span>{formatDate(event.occurred_at, true, locale, t("Pa afat"))}</span>{event.details && Object.keys(event.details).length ? <small>{JSON.stringify(event.details)}</small> : null}</div></article>)}{!events.length ? <div className="case-empty-state">Aktiviteti do të regjistrohet këtu.</div> : null}</div> : null}
        </div>
      </aside>
    </div>
  );
}

function TasksWorkspace({ data, onOpen, onRefresh, notify }) {
  const { locale, t } = useI18n();
  const [filter, setFilter] = useState("OPEN");
  const casesById = new Map(data.cases.rows.map((item) => [item.id, item]));
  const rows = data.tasks.rows.filter((task) => filter === "ALL" || (filter === "OPEN" ? isOpenTask(task) : task.status === filter));
  const sorted = [...rows].sort((a, b) => {
    if (!a.due_at) return 1; if (!b.due_at) return -1;
    return new Date(a.due_at) - new Date(b.due_at);
  });

  const changeStatus = async (task, status) => {
    try {
      await updateRow("case_tasks", task.id, { status, completed_at: status === "DONE" ? new Date().toISOString() : null });
      notify("success", "Detyra u përditësua."); await onRefresh();
    } catch (error) { notify("error", error.message); }
  };

  return (
    <>
      <SetupNotice dataset={data.tasks} />
      {data.tasks.table ? <section className="admin-panel global-task-panel"><div className="admin-panel__heading"><div><span>Planifikimi</span><h2>Detyrat e ekipit</h2><p>{rows.filter(isOpenTask).length} detyra të hapura</p></div><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="OPEN">Të hapura</option><option value="ALL">Të gjitha</option>{TASK_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></div><div className="admin-table-wrap"><table className="admin-table task-table"><thead><tr><th>Detyra</th><th>Rasti</th><th>Përgjegjësi</th><th>Afati</th><th>Statusi</th></tr></thead><tbody>{sorted.map((task) => { const item = casesById.get(task.case_id); const overdue = isOpenTask(task) && isOverdue(task.due_at); return <tr key={task.id} className={overdue ? "task-row--overdue" : ""}><td><strong>{task.title}</strong><small>{priorityLabel(task.priority)}</small></td><td><button className="case-reference-link" type="button" onClick={() => onOpen(task.case_id)}>{item?.reference || "—"}</button><small>{item?.client_name || ""}</small></td><td>{staffLabel(data.staff.rows, task.assigned_to)}</td><td><span className={overdue ? "task-due--overdue" : ""}>{formatDate(task.due_at, true, locale, t("Pa afat"))}</span></td><td><select value={task.status} onChange={(event) => changeStatus(task, event.target.value)}>{TASK_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></td></tr>; })}</tbody></table>{!sorted.length ? <div className="case-empty-state">Nuk ka detyra në këtë filtër.</div> : null}</div></section> : null}
    </>
  );
}

export default function CaseManagement({ view, data, userId, selectedCaseId, onOpenCase, onNavigate, onRefresh, notify }) {
  if (view === "dashboard") return <Dashboard data={data} onNavigate={onNavigate} onOpen={(id) => { onOpenCase(id); if (id) onNavigate("cases"); }} />;
  if (view === "cases") return <CasesWorkspace data={data} userId={userId} selectedCaseId={selectedCaseId} onOpen={onOpenCase} onRefresh={onRefresh} notify={notify} />;
  if (view === "tasks") return <><TasksWorkspace data={data} onOpen={(id) => { onOpenCase(id); onNavigate("cases"); }} onRefresh={onRefresh} notify={notify} />{selectedCaseId ? <CaseDrawer caseId={selectedCaseId} data={data} userId={userId} onClose={() => onOpenCase(null)} onRefresh={onRefresh} notify={notify} /> : null}</>;
  return null;
}
